import { useBlocker } from '@tanstack/react-router';
import { isDirty as isDirtyValue } from './form-state.ts';
import { type UnsavedStore, createUnsavedStore } from './unsaved-store.ts';
import { createContext, useCallback, useContext, useEffect, useId, useRef, useState, useSyncExternalStore, type ReactNode } from 'react';

/**
 * Ungespeicherte Änderungen (EXPERIENCE §4.3, Story 1.23): Formulare eines Datensatzes melden, ob sie geändert sind, und
 * wie man sie speichert. Wer den Datensatz schließen oder wechseln will (Panel ✕/Esc/Abdunklung, ↑ ↓, „Vollansicht“,
 * „Zurück“, Router-Navigation inkl. Browser-Zurück und Seitenleiste), fragt `attempt(aktion)`: ohne Änderungen läuft die
 * Aktion sofort; mit Änderungen bleibt alles offen und die Leiste „Nicht gespeichert · Speichern · Verwerfen“ erscheint
 * (kein zusätzliches Fenster). „Verwerfen“ führt die Aktion aus, „Speichern“ speichert die geänderten Formulare und
 * lässt den Datensatz offen. Neu laden bzw. Tab schließen fragt der Browser selbst (beforeunload).
 */

export { createUnsavedStore, type UnsavedEntry, type UnsavedStore } from './unsaved-store.ts';
export { isDirty } from './form-state.ts';

const UnsavedContext = createContext<UnsavedStore | null>(null);

/** Alle offenen Datensatz-Bereiche (für Aktionen außerhalb des Datensatzes, z. B. „Abmelden“). */
const activeStores = new Set<UnsavedStore>();

/**
 * Aktion außerhalb eines Datensatzes (z. B. „Abmelden“ in Seitenleiste oder Schublade): hat ein offener Datensatz
 * ungespeicherte Änderungen, hält sie an und zeigt dort die Leiste; sonst läuft sie sofort.
 */
export function attemptLeave(action: () => void): void {
  const dirty = [...activeStores].find((s) => s.isDirty() && !s.bypassing());
  if (dirty) dirty.attempt(action);
  else action();
}

/** Suchparameter für Sprünge innerhalb einer Seite (Einstellungs-Suche, Rudolf 06.10.2026). */
const JUMP_PARAMS = new Set(['feld', 'karte']);

/** Gleicher Pfad und gleiche Suchparameter außer den Sprung-Parametern? */
function isJumpOnly(current: { pathname: string; search: unknown }, next: { pathname: string; search: unknown }): boolean {
  if (current.pathname !== next.pathname) return false;
  const rest = (search: unknown) =>
    JSON.stringify(Object.entries((search ?? {}) as Record<string, unknown>).filter(([k]) => !JUMP_PARAMS.has(k)).sort(([a], [b]) => a.localeCompare(b)));
  return rest(current.search) === rest(next.search);
}

/** Bereich eines Datensatzes (Seite oder Panel): eigener Speicher, Router-Blocker und beforeunload. */
export function UnsavedScope({ store, children }: { store: UnsavedStore; children: ReactNode }) {
  const blocker = useBlocker({
    // Ein Sprung auf derselben Seite (Einstellungs-Suche: nur `?feld=…`/`?karte=…` ändert sich) lässt die Eingaben
    // stehen; jeder andere Wechsel (Pfad oder übrige Suchparameter) fragt wie bisher.
    shouldBlockFn: ({ current, next }) => !isJumpOnly(current, next) && store.isDirty() && !store.bypassing(),
    enableBeforeUnload: () => store.isDirty(),
    withResolver: true,
  });
  const { status, proceed, reset } = blocker;
  useEffect(() => {
    activeStores.add(store);
    return () => {
      activeStores.delete(store);
    };
  }, [store]);
  useEffect(() => {
    if (status === 'blocked') store.setRouterBlock({ proceed, reset });
  }, [status, proceed, reset, store]);
  return <UnsavedContext.Provider value={store}>{children}</UnsavedContext.Provider>;
}

/** Neuer Speicher für einen Datensatz-Bereich (stabil für die Lebensdauer der Komponente). */
export function useUnsavedStore(): UnsavedStore {
  const [store] = useState(createUnsavedStore);
  return store;
}

/** Speicher des umgebenden Datensatzes (oder `null` außerhalb). */
export function useUnsaved(): UnsavedStore | null {
  return useContext(UnsavedContext);
}

/** Ohne umgebenden Datensatz: Aktion sofort. */
export function useAttempt(): (action: () => void) => void {
  const store = useUnsaved();
  return useCallback((action: () => void) => (store ? store.attempt(action) : action()), [store]);
}

/**
 * Formular mit Wächter: vergleicht seinen GANZEN Wert (`value`, alle Felder als ein Objekt) mit dem Wert beim Öffnen des
 * Formulars (nach Speichern/Rückgängig wird es neu aufgebaut) und meldet den Stand an den Datensatz. Liefert „geändert“.
 * `submit = null`: Entwurf, der sich nicht speichern lässt.
 */
export function useUnsavedValue(value: unknown, submit: (() => void) | null): boolean {
  const [initial] = useState(value);
  const dirty = isDirtyValue(initial, value);
  useUnsavedForm(dirty, submit);
  return dirty;
}

/**
 * Formular meldet seinen Änderungsstand und wie es gespeichert wird (z. B. `form.requestSubmit()`); `null` = Entwurf,
 * der sich nicht speichern lässt (bleibt bei „Speichern“ stehen).
 */
export function useUnsavedForm(dirty: boolean, submit: (() => void) | null, title?: string): void {
  const store = useUnsaved();
  const id = useId();
  const submitRef = useRef(submit);
  useEffect(() => {
    submitRef.current = submit;
  });
  const savable = submit !== null;
  useEffect(() => {
    store?.setEntry(id, { dirty, submit: savable ? () => submitRef.current?.() : null, title });
  }, [store, id, dirty, savable, title]);
  useEffect(() => () => store?.setEntry(id, null), [store, id]);
}

/**
 * Leiste „Nicht gespeichert · Speichern · Verwerfen“ (EXPERIENCE §4.3): erscheint, solange ein Verlassen mit Änderungen
 * angehalten ist; am Handy unten über der Seite fixiert, sonst oben im Datensatz (beim Scrollen stehend). Melden die
 * geänderten Formulare einen Namen (Karten-Formular), steht er dabei: „Nicht gespeichert: Briefkopf, Bank · Alle
 * speichern · Verwerfen“ (Change 04.10.2026 Formulare kompakt).
 */
export function UnsavedBar({ phone }: { phone: boolean }) {
  const store = useUnsaved();
  const shown = useSyncExternalStore(
    (l) => store?.subscribe(l) ?? (() => {}),
    () => store?.prompting() ?? false,
  );
  const canSave = useSyncExternalStore(
    (l) => store?.subscribe(l) ?? (() => {}),
    () => store?.canSave() ?? false,
  );
  const titles = useSyncExternalStore(
    (l) => store?.subscribe(l) ?? (() => {}),
    () => (store?.dirtyTitles() ?? []).join('\u0000'),
  )
    .split('\u0000')
    .filter(Boolean);
  if (!store || !shown) return null;
  return (
    <div
      role="region"
      aria-label="Nicht gespeichert"
      data-testid="nicht-gespeichert"
      className={
        phone
          ? 'fixed inset-x-4 bottom-4 z-40 flex items-center gap-3 rounded-card bg-[var(--glass)] py-2 pr-2 pl-4 text-row shadow-float backdrop-blur-[16px]'
          : 'sticky top-2 z-20 flex items-center gap-3 rounded-full bg-[var(--glass)] py-2 pr-2 pl-5 text-row shadow-float backdrop-blur-[16px]'
      }
    >
      <span role="status" className="mr-auto text-ink">
        {titles.length > 0 ? `Nicht gespeichert: ${titles.join(', ')}` : 'Nicht gespeichert'}
      </span>
      <button
        type="button"
        onClick={() => (canSave ? store.save() : store.keepEditing())}
        className="inline-flex h-9 cursor-pointer items-center rounded-full bg-primary px-4 text-primary-foreground pointer-coarse:h-11"
      >
        {/* Nur ein E-Mail-Entwurf geändert: nichts zu speichern (Senden wäre Außenwirkung) – weiter bearbeiten. */}
        {canSave ? (titles.length > 1 ? 'Alle speichern' : 'Speichern') : 'Weiter bearbeiten'}
      </button>
      <button
        type="button"
        onClick={() => store.discard()}
        className="inline-flex h-9 cursor-pointer items-center rounded-full bg-secondary px-4 text-secondary-foreground pointer-coarse:h-11"
      >
        Verwerfen
      </button>
    </div>
  );
}
